/**
 * Layout chinh - Application Layout Component
 *
 * Chuc nang:
 * - Sidebar: Menu dan nuong, nguoi dung, hanh dong
 * - Header: Logo, thong bao, nguoi dung menu
 * - Content: Outlet cho cac trang con
 * - Sidebar co rut khi responsive
 *
 * API duoc su dung:
 * - GET /api/admin/facility-info - Favicon/logo co so
 * - GET /api/notifications - Danh sach thong bao
 */
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Typography, Form, Input, Modal, message, Spin } from 'antd';
import {
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  BankOutlined,
  WalletOutlined,
  FileDoneOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import api from '../../api';
import { buildMenuTree } from '../../utils/menuBuilder';
import { applyFavicon, saveFaviconLogoPath } from '../../utils/branding';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const ASSESSMENT_REPORT_MENU_KEY = '/assessment-reports';
const SUPER_ADMIN_ROLES = new Set(['SUPER_ADMIN', 'SYSTEM_ADMIN']);

function normalizeMenuNavigateKey(rawKey: React.Key): string {
  const key = String(rawKey || '').trim();
  if (!key) return '/dashboard';
  if (key.startsWith('/')) return key;
  if (key.startsWith('reports/')) return `/${key}`;
  if (/^(work|business|inventory-sales)(\?|$)/.test(key)) {
    return `/reports/${key}`;
  }
  return `/${key}`;
}

const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [allMenuItems, setAllMenuItems] = useState<any[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [routeTransitioning, setRouteTransitioning] = useState(true);
  const [showFullPageLoading, setShowFullPageLoading] = useState(true);
  const menuScrollRef = useRef<HTMLDivElement | null>(null);
  const transitionStartedAtRef = useRef<number>(Date.now());
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    logout,
    allowedMenuKeys,
    authorizationLoading,
    ensureAuthorizationLoaded,
    getRoleName,
  } = useAuthStore();

  React.useEffect(() => {
    if (user?.fullName) {
      document.title = `${user.fullName} – CMS Clinical Management`;
    } else {
      document.title = 'CMS Clinical Management';
    }
    return () => {
      document.title = 'CMS Clinical Management';
    };
  }, [user?.fullName]);

  useEffect(() => {
    ensureAuthorizationLoaded();
    api.get('/notifications/unread-count')
      .then(({ data }) => setUnreadCount(data.count || 0))
      .catch(() => setUnreadCount(0));
  }, [user?.role, ensureAuthorizationLoaded]);

  useEffect(() => {
    api.get('/admin/facility-info')
      .then(({ data }) => {
        const logoPath = data?.logoPath ? String(data.logoPath) : null;
        applyFavicon(logoPath);
        saveFaviconLogoPath(logoPath);
      })
      .catch(() => {
        // Keep cached/default favicon when facility info cannot be loaded.
      });
  }, []);

  useEffect(() => {
    const loadMenus = async () => {
      try {
        setMenuLoading(true);
        const menuResponse = await api.get('/admin/roles/items/list');
        setAllMenuItems(menuResponse.data || []);
      } catch (error) {
        console.error('Error loading menu:', error);
        setAllMenuItems([]);
      } finally {
        setMenuLoading(false);
      }
    };

    loadMenus();
  }, []);

  const menuItems = useMemo(() => {
    const dynamicItems = buildMenuTree(allMenuItems, allowedMenuKeys) as any[];
    const userRole = String(user?.role || '').trim();
    const shouldShowAssessmentReportMenu = userRole && !SUPER_ADMIN_ROLES.has(userRole);

    if (shouldShowAssessmentReportMenu) {
      const hasAssessmentReportMenu = dynamicItems.some((item) => {
        if (!item) return false;
        if (String(item.key || '') === ASSESSMENT_REPORT_MENU_KEY) return true;
        if (Array.isArray(item.children)) {
          return item.children.some(
            (child: any) => String(child?.key || '') === ASSESSMENT_REPORT_MENU_KEY,
          );
        }
        return false;
      });

      if (!hasAssessmentReportMenu) {
        const casesRoot = dynamicItems.find((item) => String(item?.key || '') === 'cases');
        if (casesRoot) {
          const nextChildren = Array.isArray(casesRoot.children) ? [...casesRoot.children] : [];
          nextChildren.push({ key: ASSESSMENT_REPORT_MENU_KEY, label: 'Danh sách bản đánh giá' });
          casesRoot.children = nextChildren;
        } else {
          dynamicItems.push({
            key: ASSESSMENT_REPORT_MENU_KEY,
            label: 'Danh sách bản đánh giá',
            icon: <FileDoneOutlined />,
          });
        }
      }
    }

    const hasDashboardItem = dynamicItems.some((item: any) => String(item?.key || '') === '/dashboard');

    if (hasDashboardItem) {
      return dynamicItems;
    }

    return [
      {
        key: '/dashboard',
        label: 'Trang chủ',
        icon: <DashboardOutlined />,
      },
      ...dynamicItems,
    ];
  }, [allMenuItems, allowedMenuKeys, user?.role]);

  const defaultOpenKeys = useMemo(
    () => menuItems
      .filter((item): item is NonNullable<typeof item> & { children: unknown[]; key: React.Key } =>
        item != null && 'children' in item && Array.isArray((item as any).children) && (item as any).children.length > 0
      )
      .map((item) => String(item.key)),
    [menuItems]
  );

  const roleLabel = getRoleName(user?.role);
  const contentLoader = (
    <div className="cms-route-loading-overlay" aria-live="polite" aria-busy="true">
      <Spin size="large" tip="Đang tải dữ liệu..." />
    </div>
  );

  useEffect(() => {
    transitionStartedAtRef.current = Date.now();
    setRouteTransitioning(true);
    setShowFullPageLoading(true);
  }, [location.pathname]);

  useEffect(() => {
    const isReady = !routeTransitioning || (
      !menuLoading &&
      !authorizationLoading
    );

    if (!isReady) {
      setShowFullPageLoading(true);
      return;
    }

    const elapsed = Date.now() - transitionStartedAtRef.current;
    const minVisibleMs = 260;
    const waitMs = Math.max(0, minVisibleMs - elapsed);
    const timerId = window.setTimeout(() => {
      setRouteTransitioning(false);
      setShowFullPageLoading(false);
    }, waitMs);

    return () => window.clearTimeout(timerId);
  }, [routeTransitioning, menuLoading, authorizationLoading]);

  useEffect(() => {
    const syncSelectedMenuIntoView = () => {
      const selectedItem = menuScrollRef.current?.querySelector<HTMLElement>(
        '.ant-menu-item-selected, .ant-menu-submenu-selected > .ant-menu-submenu-title'
      );
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    };

    const timerId = window.setTimeout(() => {
      window.requestAnimationFrame(syncSelectedMenuIntoView);
    }, 80);

    return () => window.clearTimeout(timerId);
  }, [location.pathname, collapsed, menuLoading, authorizationLoading, menuItems.length]);

  const userMenu: any[] = [
    {
      key: 'profile-summary',
      disabled: true,
      label: (
        <div style={{ minWidth: 220 }}>
          <div style={{ fontWeight: 700 }}>{user?.fullName}</div>
          <div>{user?.jobTitle || roleLabel}</div>
          <div style={{ color: '#8c8c8c' }}>{user?.email || 'Chưa có email'}</div>
        </div>
      ),
    },
    { type: 'divider' },
    { key: 'change-password', icon: <WalletOutlined />, label: 'Đổi mật khẩu', onClick: () => setPasswordOpen(true) },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: () => { logout(); navigate('/login'); } },
  ];

  const savePassword = async () => {
    try {
      const values = await form.validateFields();
      setSavingPassword(true);
      await api.put('/auth/me/password', values);
      message.success('Đổi mật khẩu thành công');
      setPasswordOpen(false);
      form.resetFields();
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err.response?.data?.message || 'Không đổi được mật khẩu');
      }
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Layout className="cms-app-layout" style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        className="cms-app-sider"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={280}
        theme="dark"
        style={{ height: '100vh', overflow: 'hidden' }}
      >
        <div style={{ height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <BankOutlined style={{ color: '#1677ff', fontSize: 24 }} />
          {!collapsed && <Text style={{ color: '#fff', marginLeft: 10, fontWeight: 700, fontSize: 15 }}>CMS Clinical Management</Text>}
        </div>
        <div ref={menuScrollRef} className="cms-sider-scroll">
          <Spin spinning={menuLoading || authorizationLoading} style={{ width: '100%', padding: '20px 0' }}>
            <Menu
              className="cms-side-menu"
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              defaultOpenKeys={defaultOpenKeys}
              items={menuItems}
              onClick={({ key }) => navigate(normalizeMenuNavigateKey(key))}
              style={{ paddingTop: 8, borderInlineEnd: 'none' }}
            />
          </Spin>
        </div>
      </Sider>

      <Layout className="cms-main-layout" style={{ minHeight: 0 }}>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
          <Space>
            <FileDoneOutlined style={{ color: '#1677ff' }} />
            <Text strong>{roleLabel}</Text>
          </Space>
          <Space size={16}>
            <Badge count={unreadCount} size="small">
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => navigate('/notifications')} />
            </Badge>
            <Dropdown menu={{ items: userMenu }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar src={user?.avatarPath || undefined} icon={<UserOutlined />} size="small" />
                <div style={{ lineHeight: 1.2 }}>
                  <div><Text strong>{user?.fullName}</Text></div>
                  <div><Text type="secondary" style={{ fontSize: 12 }}>{user?.jobTitle || roleLabel}</Text></div>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content className="cms-app-content" style={{ margin: '24px', background: '#f5f5f5', minHeight: 0, overflow: 'auto', position: 'relative' }}>
          <Suspense fallback={contentLoader}>
            <Outlet />
          </Suspense>
          {showFullPageLoading && (
            contentLoader
          )}
        </Content>
      </Layout>

      <Modal centered open={passwordOpen} title="Đổi mật khẩu" onCancel={() => setPasswordOpen(false)} onOk={savePassword} okText="Lưu" confirmLoading={savingPassword}>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="currentPassword" label="Mật khẩu hiện tại" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="newPassword" label="Mật khẩu mới" rules={[{ required: true }, { min: 6 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AppLayout;
